import React, { useState, useEffect } from "react";
import { getJobVersions } from "../services/apiService";
import "./VersionHistoryViewer.css";

// Helper function to format currency values consistently
const formatCurrency = (value) => {
  // Handle undefined, null, or empty string
  if (value === undefined || value === null || value === "") {
    return "0.00";
  }

  // If it's already a string with a dollar sign, return as is
  if (typeof value === "string" && value.startsWith("$")) {
    return value;
  }

  // Convert to number if it's a string
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  // Check if it's a valid number
  if (isNaN(numValue)) {
    return "0.00";
  }

  // Format to 2 decimal places
  return numValue.toFixed(2);
};

function VersionHistoryViewer({ jobId, onVersionSelect, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchVersions() {
      try {
        setLoading(true);
        const versionsData = await getJobVersions(jobId);

        // Log all versions data to console for debugging
        console.log("API returned versions data:", versionsData);

        setVersions(versionsData);
      } catch (err) {
        console.error("Error fetching versions:", err);
        setError(err.message || "Failed to load version history");
      } finally {
        setLoading(false);
      }
    }

    if (jobId) {
      fetchVersions();
    }
  }, [jobId]);

  return (
    <div className="version-history-viewer">
      <div className="version-history-header">
        <h2>Version History</h2>
        <button
          type="button"
          className="wb-plain close-button"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {loading && <p>Loading versions...</p>}
      {error && <p className="error-message">{error}</p>}

      {!loading &&
        !error &&
        (versions.length > 0 ? (
          <div className="version-table-container">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Name</th>
                  <th>Edited By</th>
                  <th>Date</th>
                  <th>Change Note</th>
                  <th>Final Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => {
                  // Determine the price source
                  let priceToShow = version.final_quoted_price; // Default to top-level field
                  // Use the correct key from the calculation_results dictionary
                  if (
                    version.calculation_results &&
                    version.calculation_results
                      .final_suggested_sale_price_P105 !== undefined
                  ) {
                    // Prefer the price from the detailed results if available
                    priceToShow =
                      version.calculation_results
                        .final_suggested_sale_price_P105;
                  }

                  // Debug log to see which price is being used
                  console.log(
                    `Version ${version.version_number}: Using price ${priceToShow} (Source: ${version.calculation_results ? "calc_results.final_suggested_sale_price_P105" : "top_level"})`,
                  );

                  return (
                    <tr key={version.id}>
                      <td>{version.version_number}</td>
                      <td>{version.version_name}</td>
                      <td>{version.created_by}</td>
                      <td>{new Date(version.created_at).toLocaleString()}</td>
                      <td className="change-note-cell">
                        {version.change_note || "No change note"}
                      </td>
                      <td className="price-cell">
                        {/* Format the determined price */}$
                        {formatCurrency(priceToShow)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="wb-plain load-version-button"
                          onClick={() => onVersionSelect(version)}
                        >
                          Load
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No version history found.</p>
        ))}
    </div>
  );
}

export default VersionHistoryViewer;
